import * as THREE from 'three';
import { createSeededRandom } from '../utils/random';

/**
 * Pooled surface foam for the ocean: flat whitecap/foam decals that ride the
 * shared Gerstner surface plus a mist of bow spray thrown up by the hull.
 *
 * Both effects reuse fixed buffers created once at construction, so nothing is
 * allocated per frame. Whitecap decals are seeded around the ship and placed on
 * local wave crests (sampled through the `waveHeight` callback), so they follow
 * the same deterministic field as the GPU water and the server buoyancy. Hull
 * foam and spray are emitted at the waterline/bow along the direction of travel
 * and settle back onto the surface.
 *
 * All randomness flows through `createSeededRandom`, matching the rest of the
 * project: captures and bot playtests stay reproducible (no `Math.random`).
 */

const SEED = 0xf0a6;
const DECAL_COUNT = 96;
const SPRAY_COUNT = 128;

/** Annulus around the ship where ambient whitecaps are distributed. */
const WHITECAP_MIN_RADIUS = 5;
const WHITECAP_MAX_RADIUS = 42;
/** Perspective point-size scale for the spray mist. */
const SPRAY_SCALE = 700;

/** Bow offset ahead of the ship origin (bow is +X). */
const BOW_OFFSET = 5.2;
const HULL_HALF_WIDTH = 1.95;
const HULL_FOAM_MIN_X = -3.0;
const HULL_FOAM_MAX_X = 4.2;

const GRAVITY = 5.0;
const DRAG = 2.2;

const MIN_SPRAY_SPEED = 0.5;
const FULL_SPRAY_SPEED = 7;

const WHITECAP_COLOR = new THREE.Color(0xf2f8ff);
const FOAM_COLOR = new THREE.Color(0xcfe9f5);

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

function smoothstep(a: number, b: number, value: number): number {
  const t = clamp01((value - a) / (b - a));
  return t * t * (3 - 2 * t);
}

interface FoamDecal {
  position: THREE.Vector3;
  age: number;
  life: number;
  size: number;
  /** Base opacity; the per-frame fade is applied when the buffer is written. */
  alpha: number;
  yaw: number;
  color: THREE.Color;
}

interface SprayParticle {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  age: number;
  life: number;
  size: number;
  alpha: number;
}

const DECAL_VERTEX = /* glsl */ `
  attribute float aAlpha;
  attribute vec3 aColor;

  varying float vAlpha;
  varying vec3 vColor;
  varying vec2 vUv;
  varying float vPhase;

  #include <fog_pars_vertex>

  void main() {
    vAlpha = aAlpha;
    vColor = aColor;
    vUv = uv;

    // Deterministic per-decal phase from the instance translation, so every
    // pooled quad gets its own rim shape instead of an identical disc.
    vec3 center = instanceMatrix[3].xyz;
    vPhase = fract(sin(dot(center.xz, vec2(12.9898, 78.233))) * 43758.5453);

    vec4 world = instanceMatrix * vec4(position, 1.0);
    vec4 mvPosition = modelViewMatrix * world;
    gl_Position = projectionMatrix * mvPosition;

    #include <fog_vertex>
  }
`;

const DECAL_FRAGMENT = /* glsl */ `
  varying float vAlpha;
  varying vec3 vColor;
  varying vec2 vUv;
  varying float vPhase;

  #include <fog_pars_fragment>

  void main() {
    if (vAlpha <= 0.004) discard;

    // Irregular soft blob: multi-frequency lobes and cellular micro-bubbles rotate
    // with the decal's phase so overlapping quads read as a foam field, not a repeated circular tile.
    vec2 uv = vUv * 2.0 - 1.0;
    float radial = dot(uv, uv);
    float angle = atan(uv.y, uv.x);
    float rim = 1.0
      + 0.28 * sin(angle * 2.0 + vPhase * 6.28318)
      + 0.18 * sin(angle * 3.0 - vPhase * 4.18879)
      + 0.10 * sin(angle * 5.0 + vPhase * 11.23);
    float soft = smoothstep(rim, rim * 0.18, radial);
    float cellular = sin(uv.x * 14.13 + vPhase * 6.28) * sin(uv.y * 14.13 - vPhase * 3.14);
    float cellularFine = sin(uv.x * 29.7 + uv.y * 19.3 + vPhase * 8.9);
    soft *= (0.75 + 0.25 * cellular) * (0.85 + 0.15 * cellularFine);
    if (soft <= 0.004) discard;

    gl_FragColor = vec4(vColor, vAlpha * soft);

    #include <fog_fragment>
  }
`;

const SPRAY_VERTEX = /* glsl */ `
  attribute float aAlpha;
  attribute float aSize;

  uniform float uScale;

  varying float vAlpha;

  #include <fog_pars_vertex>

  void main() {
    vAlpha = aAlpha;
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    float depth = -mvPosition.z;
    gl_PointSize = (aAlpha > 0.0 && depth > 0.0) ? aSize * (uScale / depth) : 0.0;
    gl_Position = projectionMatrix * mvPosition;

    #include <fog_vertex>
  }
`;

const SPRAY_FRAGMENT = /* glsl */ `
  varying float vAlpha;

  #include <fog_pars_fragment>

  void main() {
    if (vAlpha <= 0.002) discard;

    vec2 uv = gl_PointCoord - vec2(0.5);
    float disc = smoothstep(0.5, 0.16, length(uv));
    if (disc <= 0.004) discard;

    gl_FragColor = vec4(1.0, 1.0, 1.0, vAlpha * disc);

    #include <fog_fragment>
  }
`;

export class WaterFoam {
  private readonly decalMesh: THREE.InstancedMesh;
  private readonly decalMaterial: THREE.ShaderMaterial;
  private readonly sprayPoints: THREE.Points;
  private readonly sprayMaterial: THREE.ShaderMaterial;

  private readonly decals: FoamDecal[] = [];
  private readonly decalAlpha: Float32Array;
  private readonly decalColor: Float32Array;
  private readonly decalAlphaAttr: THREE.InstancedBufferAttribute;
  private readonly decalColorAttr: THREE.InstancedBufferAttribute;

  private readonly spray: SprayParticle[] = [];
  private readonly sprayPositions: Float32Array;
  private readonly sprayAlphas: Float32Array;
  private readonly spraySizes: Float32Array;
  private readonly sprayPositionAttr: THREE.BufferAttribute;
  private readonly sprayAlphaAttr: THREE.BufferAttribute;
  private readonly spraySizeAttr: THREE.BufferAttribute;

  private readonly rng = createSeededRandom(SEED);
  private readonly matrix = new THREE.Matrix4();
  private readonly quaternion = new THREE.Quaternion();
  private readonly scale = new THREE.Vector3();
  private readonly up = new THREE.Vector3(0, 1, 0);
  private readonly prevPos = new THREE.Vector3();

  private decalCursor = 0;
  private sprayCursor = 0;
  private whitecapAccumulator = 0;
  private hullFoamAccumulator = 0;
  private sprayAccumulator = 0;

  private foam = 0;
  private sprayAmount = 0;
  private whitecap = 0;
  private waveHeight: (x: number, z: number) => number = () => 0;
  private dirX = 1;
  private dirZ = 0;
  private hasPrevPos = false;

  constructor(parent: THREE.Object3D) {
    // --- whitecap/foam decals: one InstancedMesh of flat quads -------------
    const quad = new THREE.BufferGeometry();
    quad.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(
        [-0.5, 0, -0.5, 0.5, 0, -0.5, 0.5, 0, 0.5, -0.5, 0, 0.5],
        3,
      ),
    );
    quad.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
    quad.setIndex([0, 1, 2, 0, 2, 3]);

    this.decalAlpha = new Float32Array(DECAL_COUNT);
    this.decalColor = new Float32Array(DECAL_COUNT * 3);
    this.decalAlphaAttr = new THREE.InstancedBufferAttribute(this.decalAlpha, 1);
    this.decalAlphaAttr.setUsage(THREE.DynamicDrawUsage);
    this.decalColorAttr = new THREE.InstancedBufferAttribute(this.decalColor, 3);
    this.decalColorAttr.setUsage(THREE.DynamicDrawUsage);
    quad.setAttribute('aAlpha', this.decalAlphaAttr);
    quad.setAttribute('aColor', this.decalColorAttr);

    this.decalMaterial = new THREE.ShaderMaterial({
      uniforms: THREE.UniformsUtils.merge([THREE.UniformsLib.fog]),
      vertexShader: DECAL_VERTEX,
      fragmentShader: DECAL_FRAGMENT,
      transparent: true,
      depthWrite: false,
      fog: true,
      side: THREE.DoubleSide,
    });

    this.decalMesh = new THREE.InstancedMesh(quad, this.decalMaterial, DECAL_COUNT);
    this.decalMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.decalMesh.frustumCulled = false;
    this.decalMesh.renderOrder = 1;
    this.decalMesh.name = 'water-foam-decals';
    parent.add(this.decalMesh);

    for (let i = 0; i < DECAL_COUNT; i += 1) {
      this.decals.push({
        position: new THREE.Vector3(0, -1000, 0),
        age: 0,
        life: 0,
        size: 1,
        alpha: 0,
        yaw: 0,
        color: new THREE.Color(),
      });
      this.matrix.makeScale(0, 0, 0);
      this.decalMesh.setMatrixAt(i, this.matrix);
    }

    // --- bow spray: one Points draw call -----------------------------------
    this.sprayPositions = new Float32Array(SPRAY_COUNT * 3);
    this.sprayAlphas = new Float32Array(SPRAY_COUNT);
    this.spraySizes = new Float32Array(SPRAY_COUNT);

    const sprayGeometry = new THREE.BufferGeometry();
    this.sprayPositionAttr = dynamic(new THREE.BufferAttribute(this.sprayPositions, 3));
    this.sprayAlphaAttr = dynamic(new THREE.BufferAttribute(this.sprayAlphas, 1));
    this.spraySizeAttr = dynamic(new THREE.BufferAttribute(this.spraySizes, 1));
    sprayGeometry.setAttribute('position', this.sprayPositionAttr);
    sprayGeometry.setAttribute('aAlpha', this.sprayAlphaAttr);
    sprayGeometry.setAttribute('aSize', this.spraySizeAttr);
    sprayGeometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), Infinity);

    this.sprayMaterial = new THREE.ShaderMaterial({
      uniforms: THREE.UniformsUtils.merge([
        THREE.UniformsLib.fog,
        { uScale: { value: SPRAY_SCALE } },
      ]),
      vertexShader: SPRAY_VERTEX,
      fragmentShader: SPRAY_FRAGMENT,
      transparent: true,
      depthWrite: false,
      fog: true,
    });

    this.sprayPoints = new THREE.Points(sprayGeometry, this.sprayMaterial);
    this.sprayPoints.frustumCulled = false;
    this.sprayPoints.renderOrder = 2;
    this.sprayPoints.name = 'water-foam-spray';
    parent.add(this.sprayPoints);

    for (let i = 0; i < SPRAY_COUNT; i += 1) {
      this.spray.push({
        position: new THREE.Vector3(0, -1000, 0),
        velocity: new THREE.Vector3(),
        age: 0,
        life: 0,
        size: 0,
        alpha: 0,
      });
      this.sprayPositions[i * 3 + 1] = -1000;
    }
  }

  /** Drive emission with the sea state: crest foam, spray and whitecaps. */
  setSeaState(config: { foam: number; spray: number; whitecap: number }): void {
    this.foam = clamp01(config.foam);
    this.sprayAmount = clamp01(config.spray);
    this.whitecap = clamp01(config.whitecap);
  }

  /**
   * Advance foam for one frame.
   *
   * @param delta       Seconds since the previous update.
   * @param shipPos     Ship origin in world space.
   * @param speed       Forward speed (m/s); drives hull foam and spray.
   * @param waveHeight  Water height sampler `(x, z) => y` (the shared field).
   */
  update(
    delta: number,
    shipPos: THREE.Vector3,
    speed: number,
    waveHeight: (x: number, z: number) => number,
  ): void {
    const dt = Math.min(Math.max(delta, 0), 0.05);
    this.waveHeight = waveHeight;

    // Track the direction of travel for bow spray; keep the last known heading
    // when the hull is (nearly) stationary.
    if (this.hasPrevPos) {
      const dx = shipPos.x - this.prevPos.x;
      const dz = shipPos.z - this.prevPos.z;
      const length = Math.hypot(dx, dz);
      if (length > 0.02) {
        this.dirX = dx / length;
        this.dirZ = dz / length;
      }
    } else {
      this.hasPrevPos = true;
    }
    this.prevPos.copy(shipPos);

    this.emit(dt, shipPos.x, shipPos.z, speed);
    this.integrate(dt);
    this.writeDecals();
    this.writeSpray();
  }

  dispose(): void {
    this.decalMesh.removeFromParent();
    this.decalMesh.geometry.dispose();
    this.decalMaterial.dispose();
    this.sprayPoints.removeFromParent();
    this.sprayPoints.geometry.dispose();
    this.sprayMaterial.dispose();
    this.decals.length = 0;
    this.spray.length = 0;
  }

  // --- emission ----------------------------------------------------------

  private emit(dt: number, shipX: number, shipZ: number, speed: number): void {
    const speedFactor = clamp01((speed - MIN_SPRAY_SPEED) / (FULL_SPRAY_SPEED - MIN_SPRAY_SPEED));

    this.whitecapAccumulator += 26 * this.whitecap * dt;
    while (this.whitecapAccumulator >= 1) {
      this.whitecapAccumulator -= 1;
      this.spawnWhitecap(shipX, shipZ);
    }

    this.hullFoamAccumulator += 9 * speedFactor * this.foam * dt;
    while (this.hullFoamAccumulator >= 1) {
      this.hullFoamAccumulator -= 1;
      this.spawnHullFoam(shipX, shipZ);
    }

    this.sprayAccumulator += (16 + 26 * speedFactor) * this.sprayAmount * dt;
    while (this.sprayAccumulator >= 1) {
      this.sprayAccumulator -= 1;
      this.spawnSpray(shipX, shipZ, speed);
    }
  }

  /** Ambient whitecap on a local wave crest inside the ring around the ship. */
  private spawnWhitecap(shipX: number, shipZ: number): void {
    const angle = this.rng() * Math.PI * 2;
    const radius = WHITECAP_MIN_RADIUS + this.rng() * (WHITECAP_MAX_RADIUS - WHITECAP_MIN_RADIUS);
    const x = shipX + Math.cos(angle) * radius;
    const z = shipZ + Math.sin(angle) * radius;

    // Only brighten where the sampled field has a local crest; flat water
    // receives a faint sheen instead of a full whitecap.
    const peak = smoothstep(0.02, 0.3, this.crestFactor(x, z));

    const decal = this.decals[this.decalCursor];
    this.decalCursor = (this.decalCursor + 1) % DECAL_COUNT;

    decal.position.set(x, this.waveHeight(x, z) + 0.14, z);
    decal.age = 0;
    decal.life = 1.5 + this.rng() * 1.7;
    decal.size = 0.9 + this.rng() * 1.1 + peak * 0.7;
    decal.alpha = (0.18 + 0.6 * peak) * this.whitecap;
    decal.yaw = this.rng() * Math.PI;
    decal.color.copy(WHITECAP_COLOR).lerp(FOAM_COLOR, 1 - peak * 0.5);
  }

  /** Foam pressed out where the hull meets the water, left behind as it moves. */
  private spawnHullFoam(shipX: number, shipZ: number): void {
    const side = this.rng() < 0.5 ? -1 : 1;
    const lateralX = -this.dirZ * side;
    const lateralZ = this.dirX * side;
    const along = HULL_FOAM_MIN_X + this.rng() * (HULL_FOAM_MAX_X - HULL_FOAM_MIN_X);
    const spread = HULL_HALF_WIDTH + 0.25 + this.rng() * 0.4;
    const x = shipX + this.dirX * along + lateralX * spread;
    const z = shipZ + this.dirZ * along + lateralZ * spread;

    const decal = this.decals[this.decalCursor];
    this.decalCursor = (this.decalCursor + 1) % DECAL_COUNT;

    decal.position.set(x, this.waveHeight(x, z) + 0.14, z);
    decal.age = 0;
    decal.life = 1.2 + this.rng() * 1.4;
    decal.size = 1.2 + this.rng() * 1.1;
    decal.alpha = (0.3 + this.rng() * 0.3) * this.foam;
    decal.yaw = this.rng() * Math.PI;
    decal.color.copy(FOAM_COLOR);
  }

  /** Fine mist thrown up off the bow, ballistic with drag, settling on water. */
  private spawnSpray(shipX: number, shipZ: number, speed: number): void {
    const side = this.rng() < 0.5 ? -1 : 1;
    const lateralX = -this.dirZ * side;
    const lateralZ = this.dirX * side;

    const bowX = shipX + this.dirX * BOW_OFFSET + lateralX * (this.rng() * 0.9 - 0.45);
    const bowZ = shipZ + this.dirZ * BOW_OFFSET + lateralZ * (this.rng() * 0.9 - 0.45);

    const particle = this.spray[this.sprayCursor];
    this.sprayCursor = (this.sprayCursor + 1) % SPRAY_COUNT;

    const forward = 0.6 + this.rng() * 0.8 + speed * 0.28;
    const outward = (0.35 + this.rng() * 0.75) * side;

    particle.position.set(bowX, this.waveHeight(bowX, bowZ) + 0.3, bowZ);
    particle.velocity.set(
      this.dirX * forward + lateralX * outward,
      1.1 + this.rng() * 1.7 + speed * 0.14,
      this.dirZ * forward + lateralZ * outward,
    );
    particle.age = 0;
    particle.life = 0.5 + this.rng() * 0.7;
    particle.size = 0.22 + this.rng() * 0.4;
    particle.alpha = 0.45 + this.rng() * 0.35;
  }

  /** Local crestness: centre height minus the mean of four nearby samples. */
  private crestFactor(x: number, z: number): number {
    const eps = 1.2;
    const center = this.waveHeight(x, z);
    const xp = this.waveHeight(x + eps, z);
    const xm = this.waveHeight(x - eps, z);
    const zp = this.waveHeight(x, z + eps);
    const zm = this.waveHeight(x, z - eps);
    return center - (xp + xm + zp + zm) * 0.25;
  }

  // --- simulation --------------------------------------------------------

  private integrate(dt: number): void {
    const drag = Math.exp(-DRAG * dt);

    for (let i = 0; i < DECAL_COUNT; i += 1) {
      const decal = this.decals[i];
      if (decal.life <= 0 || decal.age >= decal.life) {
        this.decalAlpha[i] = 0;
        continue;
      }
      decal.age += dt;
      const surface = this.waveHeight(decal.position.x, decal.position.z) + 0.14;
      if (decal.position.y < surface) decal.position.y = surface;

      const t = Math.min(1, decal.age / decal.life);
      const fade = (1 - t) * (1 - t);
      this.decalAlpha[i] = decal.alpha * fade;
      this.decalColor[i * 3] = decal.color.r;
      this.decalColor[i * 3 + 1] = decal.color.g;
      this.decalColor[i * 3 + 2] = decal.color.b;
    }

    for (let i = 0; i < SPRAY_COUNT; i += 1) {
      const particle = this.spray[i];
      if (particle.life <= 0 || particle.age >= particle.life) {
        this.sprayAlphas[i] = 0;
        this.spraySizes[i] = 0;
        continue;
      }
      particle.age += dt;
      particle.velocity.y -= GRAVITY * dt;
      particle.velocity.x *= drag;
      particle.velocity.z *= drag;
      particle.position.x += particle.velocity.x * dt;
      particle.position.y += particle.velocity.y * dt;
      particle.position.z += particle.velocity.z * dt;

      const surface = this.waveHeight(particle.position.x, particle.position.z) + 0.12;
      if (particle.position.y < surface) {
        particle.position.y = surface;
        if (particle.velocity.y < 0) particle.velocity.y = 0;
      }

      const t = Math.min(1, particle.age / particle.life);
      const fade = (1 - t) * (1 - t);
      this.sprayPositions[i * 3] = particle.position.x;
      this.sprayPositions[i * 3 + 1] = particle.position.y;
      this.sprayPositions[i * 3 + 2] = particle.position.z;
      this.sprayAlphas[i] = particle.alpha * fade;
      this.spraySizes[i] = particle.size * (0.7 + 0.5 * t);
    }
  }

  private writeDecals(): void {
    for (let i = 0; i < DECAL_COUNT; i += 1) {
      if (this.decalAlpha[i] <= 0.004) {
        this.matrix.makeScale(0, 0, 0);
      } else {
        const decal = this.decals[i];
        this.quaternion.setFromAxisAngle(this.up, decal.yaw);
        this.scale.set(decal.size, 1, decal.size);
        this.matrix.compose(decal.position, this.quaternion, this.scale);
      }
      this.decalMesh.setMatrixAt(i, this.matrix);
    }
    this.decalMesh.instanceMatrix.needsUpdate = true;
    this.decalAlphaAttr.needsUpdate = true;
    this.decalColorAttr.needsUpdate = true;
  }

  private writeSpray(): void {
    this.sprayPositionAttr.needsUpdate = true;
    this.sprayAlphaAttr.needsUpdate = true;
    this.spraySizeAttr.needsUpdate = true;
  }
}

function dynamic<T extends THREE.BufferAttribute>(attribute: T): T {
  attribute.setUsage(THREE.DynamicDrawUsage);
  return attribute;
}
